// Explore-stats page: Interjection meter (#209).
// Visualises how interjection-heavy Question Time has been: per-day mean
// and max interjections, distribution histogram, chair-share rate, noisy
// portfolios, and a "roughest QTs" leaderboard. Sources `house_questions`
// `speaker_turns` (nested) via /api/explore/stats/qt-interjections.

import React, { useState, useEffect } from 'react';
import {
  Paper, Typography, Grid, Box, Stack,
} from '@mui/material';
import { getExploreStat } from '../../services/api';
import ExploreStatsPage from './ExploreStatsPage';
import { StatBar, StatLine, StatHistogram } from './charts';

export default function QtInterjections() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const res = await getExploreStat('qt-interjections');
        if (active) setData(res);
      } catch (e) {
        if (active) setError(e.message || 'Failed to load statistics');
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => { active = false; };
  }, []);

  const c = data?.charts || {};

  return (
    <ExploreStatsPage
      title="Interjection meter"
      description="How interrupted Question Time has been: per-day means and maxima, the distribution of interjections per question, how often the chair has had to step in, and which portfolios attract the noisiest answers. Interjections are caption turns annotated as `role=interjection`; chair turns are identified by a missing caption colour (the chair is non-partisan)."
      loading={loading}
      error={error}
      columns={data?.columns || []}
      rows={data?.table_rows || []}
      links={data?._links || []}
      csvName="qt-interjections"
    >
      {data && (
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Paper elevation={1} sx={{ p: 2, mb: 3, borderRadius: 3 }}>
              <Typography variant="h6" sx={{ mb: 1 }}>
                Interjections per question — by sitting day
              </Typography>
              <StatLine data={c.per_day || []} xKey="date"
                        keys={['Mean interjections', 'Max interjections']}
                        height={320} />
            </Paper>
          </Grid>
          <Grid item xs={12} md={6}>
            <Paper elevation={1} sx={{ p: 2, mb: 3, borderRadius: 3 }}>
              <Typography variant="h6" sx={{ mb: 1 }}>
                Distribution: interjections per question
              </Typography>
              <StatHistogram data={c.histogram || []} xKey="bucket"
                             yKey="count" height={320} />
            </Paper>
          </Grid>
          <Grid item xs={12} md={6}>
            <Paper elevation={1} sx={{ p: 2, mb: 3, borderRadius: 3 }}>
              <Typography variant="h6" sx={{ mb: 1 }}>
                Chair intervention share by day (%)
              </Typography>
              <StatBar data={c.chair_rate_by_day || []} xKey="date"
                       yKey="Chair share %" height={320} />
              <Typography variant="caption" color="text.secondary">
                Share of all caption turns attributed to the chair (Speaker /
                Deputy / Acting Speaker), identified by `color IS NULL`.
              </Typography>
            </Paper>
          </Grid>
          <Grid item xs={12} md={6}>
            <Paper elevation={1} sx={{ p: 2, mb: 3, borderRadius: 3 }}>
              <Typography variant="h6" sx={{ mb: 1 }}>
                Noisiest answer portfolios (mean interjections per Q)
              </Typography>
              <StatBar data={c.noisy_portfolios || []} xKey="portfolio"
                       yKey="mean_interjections" layout="vertical" height={360} />
            </Paper>
          </Grid>
          <Grid item xs={12}>
            <Paper elevation={1} sx={{ p: 2, mb: 3, borderRadius: 3 }}>
              <Typography variant="h6" sx={{ mb: 1 }}>
                Roughest single Qs (most-interrupted)
              </Typography>
              <Stack spacing={1}>
                {(c.rough_qt || []).map((q, i) => (
                  <Box key={i} sx={{ display: 'flex', gap: 2,
                                     borderBottom: '1px solid',
                                     borderColor: 'divider', pb: 1 }}>
                    <Box sx={{ minWidth: 110, fontVariantNumeric: 'tabular-nums' }}>
                      {q.Date}
                    </Box>
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="body2">
                        {q.Asker} → {q.Responder}{' '}
                        <Typography component="span" variant="caption"
                                     color="text.secondary">
                          ({q.Portfolio})
                        </Typography>
                      </Typography>
                    </Box>
                    <Box sx={{ minWidth: 110, textAlign: 'right' }}>
                      {q.Interjections} interjections
                    </Box>
                  </Box>
                ))}
              </Stack>
            </Paper>
          </Grid>
        </Grid>
      )}
    </ExploreStatsPage>
  );
}
